import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import {
  resolveMediaTransition,
  type MediaTransitionId,
} from './mediaTransitionPresets';

interface PhotoGalleryProps {
  photoUrls?: string[] | null;
  label?: string;
  /** Override the entrance transition. Defaults to the theme/global resolver. */
  transition?: MediaTransitionId | null;
  /** Used to pick the theme-default transition when `transition` is not set. */
  animationTheme?: string | null;
}

/**
 * Post-reveal attached-photos gallery with lightbox + download.
 * - Tap any thumbnail to open a full-screen viewer
 * - Swipe / arrow keys to navigate
 * - Download original high-quality image
 * Does NOT affect the cinematic reveal — only renders in the final state.
 */
export function PhotoGallery({ photoUrls, label = 'Photos attached', transition, animationTheme }: PhotoGalleryProps) {
  const photos = (photoUrls ?? []).filter(Boolean);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const close = useCallback(() => setOpenIndex(null), []);
  const next = useCallback(() => {
    setOpenIndex((i) => (i === null ? null : (i + 1) % photos.length));
  }, [photos.length]);
  const prev = useCallback(() => {
    setOpenIndex((i) => (i === null ? null : (i - 1 + photos.length) % photos.length));
  }, [photos.length]);

  // Keyboard nav
  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIndex, close, next, prev]);

  // Swipe nav
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    const delta = e.changedTouches[0].clientX - touchStart;
    if (Math.abs(delta) > 40) {
      if (delta < 0) next();
      else prev();
    }
    setTouchStart(null);
  };

  const download = useCallback(async (url: string, index: number) => {
    try {
      const res = await fetch(url, { mode: 'cors' });
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `say-it-now-photo-${index + 1}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (e) {
      console.warn('[PhotoGallery] download fallback', e);
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, []);

  if (photos.length === 0) return null;

  const current = openIndex !== null ? photos[openIndex] : null;

  const preset = resolveMediaTransition(transition ?? undefined, animationTheme ?? undefined);

  return (
    <motion.section
      className="px-3 sm:px-4 pt-5 pb-3 max-w-3xl mx-auto"
      initial={preset.initial as any}
      animate={preset.animate as any}
      transition={preset.transition}
      aria-label="Attached photos"
    >
      <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground text-center mb-1">
        📷 {label}
      </p>
      <p className="text-xs text-muted-foreground/80 text-center mb-4">
        Tap any photo to view or download
      </p>

      <div
        className={`grid gap-2 ${
          photos.length === 1
            ? 'grid-cols-1'
            : photos.length === 2
              ? 'grid-cols-2'
              : 'grid-cols-2 sm:grid-cols-3'
        }`}
      >
        {photos.map((url, i) => (
          <button
            key={`${url}-${i}`}
            type="button"
            onClick={() => setOpenIndex(i)}
            className="group relative aspect-square overflow-hidden rounded-xl bg-muted ring-1 ring-border hover:ring-primary/60 focus:outline-none focus:ring-2 focus:ring-primary transition"
            aria-label={`View photo ${i + 1} of ${photos.length}`}
          >
            <img
              src={url}
              alt={`Attached photo ${i + 1}`}
              loading="eager"
              fetchPriority={i === 0 ? 'high' : 'auto'}
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </button>
        ))}
      </div>

      <Dialog open={openIndex !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent
          className="max-w-none w-screen h-[100dvh] sm:h-[90vh] sm:max-w-5xl sm:w-[92vw] p-0 bg-black border-0 sm:rounded-2xl overflow-hidden"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {current && openIndex !== null && (
            <div className="relative w-full h-full flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.img
                  key={openIndex}
                  src={current}
                  alt={`Photo ${openIndex + 1} of ${photos.length}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="max-w-full max-h-full object-contain select-none"
                  draggable={false}
                />
              </AnimatePresence>

              {/* Close */}
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="absolute top-4 right-4 h-11 w-11 rounded-full bg-white/10 backdrop-blur text-white flex items-center justify-center hover:bg-white/20 transition z-20"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Counter */}
              {photos.length > 1 && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 text-xs uppercase tracking-[0.22em] text-white/80 bg-black/40 backdrop-blur px-3 py-1.5 rounded-full z-20">
                  {openIndex + 1} / {photos.length}
                </div>
              )}

              {/* Prev / Next */}
              {photos.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prev}
                    aria-label="Previous photo"
                    className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 backdrop-blur text-white flex items-center justify-center hover:bg-white/20 transition z-20"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    type="button"
                    onClick={next}
                    aria-label="Next photo"
                    className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 backdrop-blur text-white flex items-center justify-center hover:bg-white/20 transition z-20"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </>
              )}

              {/* Download */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
                <button
                  type="button"
                  onClick={() => download(current, openIndex)}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-black text-sm font-medium shadow-lg hover:bg-white/90 transition"
                >
                  <Download className="h-4 w-4" />
                  Download Photo
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </motion.section>
  );
}
